import type { CsvColumn } from '@/lib/csv'
import type { SubmissionWithPicks } from '@/lib/queries'
import { newestBySubmitter } from '@/lib/submissionPicks'
import type { Attendee, MatchOutcome } from '@/types/database'

/**
 * The thanks-for-coming export: everyone who filled the form in and matched
 * with nobody.
 *
 * Deliberately the same people the "No matches" card lists, and nobody else. In
 * particular it is not "everyone without a match": someone who never submitted
 * has not been turned down by anyone, they simply did not take part, and
 * sending them a message about their results would be the first they heard of
 * a form they never filled in.
 *
 * Columns line up with the matches export — First name, Last name, Recipient —
 * so the two files feed the same mail merge and an organiser can send both
 * halves of the evening from one template.
 */

export interface UnmatchedRow {
  firstName: string
  lastName: string
  /** Email address to send to. Empty when we hold none — see `missingEmail`. */
  recipient: string
  /** Kept for stable ordering and for naming anyone with a gap. */
  matchmakingId: string
}

export interface UnmatchedExport {
  rows: UnmatchedRow[]
  /**
   * Rows whose Recipient would go out empty. Everyone who submits gives an
   * email — the form requires it — so this is only reachable when the run is
   * older than the submissions behind it, typically because the entry was
   * deleted afterwards. Rare, but silently exporting a blank address would
   * mean somebody never hears anything.
   */
  missingEmail: UnmatchedRow[]
}

export const unmatchedColumns: CsvColumn<UnmatchedRow>[] = [
  { header: 'First name', value: (row) => row.firstName },
  { header: 'Last name', value: (row) => row.lastName },
  { header: 'Recipient', value: (row) => row.recipient },
]

/**
 * Builds the export from a saved run's outcomes.
 *
 * The outcomes are the authority on who matched, because they are what the run
 * actually decided; the attendee rows and submissions only supply names and
 * addresses. That is why this reads a run rather than recomputing from the
 * submissions — an organiser sends the results they have seen, not a fresh
 * calculation that may already differ.
 *
 * Addresses prefer what the person typed on the form over the imported record,
 * exactly as `get_match_pairs` does for matches. Eventbrite's address is
 * whatever they registered with, sometimes months earlier; the form address is
 * the one they most recently confirmed, on the night.
 */
export function buildUnmatchedExport(
  outcomes: readonly MatchOutcome[],
  attendees: readonly Attendee[],
  submissions: readonly SubmissionWithPicks[]
): UnmatchedExport {
  const attendeeById = new Map(attendees.map((a) => [a.id, a]))
  const newest = newestBySubmitter(submissions)

  const rows = outcomes
    .filter((outcome) => outcome.submitted && outcome.match_count === 0)
    .map((outcome) => {
      const attendee = attendeeById.get(outcome.attendee_id)
      if (attendee === undefined) return null

      return {
        firstName: attendee.first_name,
        lastName: attendee.last_name ?? '',
        recipient: newest.get(attendee.id)?.email ?? attendee.email ?? '',
        matchmakingId: attendee.matchmaking_id,
      }
    })
    // An outcome whose attendee has since been removed has nobody to write to.
    .filter((row): row is UnmatchedRow => row !== null)
    .sort((a, b) => a.matchmakingId.localeCompare(b.matchmakingId))

  return {
    rows,
    missingEmail: rows.filter((row) => row.recipient === ''),
  }
}
